import { createApi } from '@reduxjs/toolkit/query/react';
import {
  AvailableParticipant,
  Conversation,
  ConversationDetail,
  ConversationFilterParams,
  Message,
  SendMessageRequest,
} from '../../types/messaging-types';
import { baseQueryStub } from '../../utils';
import { MessagingService } from './messaging-service';

// A helper function to initialize the service once when needed
let messagingService: MessagingService | null = null;
const getMessagingService = () => {
  messagingService ??= new MessagingService();
  return messagingService;
};

export const messagingApi = createApi({
  reducerPath: 'messagingApi',
  baseQuery: baseQueryStub,
  tagTypes: ['Conversation', 'Message', 'UnreadCount'],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: builder => ({
    getConversations: builder.query<Conversation[], void>({
      queryFn: async () => {
        const service = getMessagingService();
        const conversations = await service.getConversations();
        return { data: conversations };
      },
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Conversation' as const, id })),
              { type: 'Conversation', id: 'LIST' },
            ]
          : [{ type: 'Conversation', id: 'LIST' }],
    }),

    getConversationById: builder.query<
      ConversationDetail,
      { id: number; skip?: number; take?: number }
    >({
      queryFn: async ({ id, skip = 0, take = 50 }) => {
        const service = getMessagingService();
        const conversation = await service.getConversationById(id, skip, take);
        return { data: conversation };
      },
      providesTags: (_result, _error, { id }) => [
        { type: 'Conversation', id },
        { type: 'Message', id: `conversation-${id}` },
      ],
    }),

    createOrGetConversation: builder.mutation<Conversation, number>({
      queryFn: async participant2Id => {
        const service = getMessagingService();
        const conversation = await service.createOrGetConversation(participant2Id);
        return { data: conversation };
      },
      invalidatesTags: [{ type: 'Conversation', id: 'LIST' }, 'UnreadCount'],
    }),

    sendMessage: builder.mutation<Message, { conversationId: number; request: SendMessageRequest }>(
      {
        queryFn: async ({ conversationId, request }) => {
          const service = getMessagingService();
          const message = await service.sendMessage(conversationId, request);
          return { data: message };
        },
        invalidatesTags: (_result, _error, { conversationId }) => [
          { type: 'Conversation', id: conversationId },
          { type: 'Conversation', id: 'LIST' },
          { type: 'Message', id: `conversation-${conversationId}` },
          'UnreadCount',
        ],
      }
    ),

    markConversationAsRead: builder.mutation<void, number>({
      queryFn: async conversationId => {
        const service = getMessagingService();
        await service.markConversationAsRead(conversationId);
        return { data: undefined };
      },
      invalidatesTags: (_result, _error, conversationId) => [
        { type: 'Conversation', id: conversationId },
        { type: 'Conversation', id: 'LIST' },
        { type: 'Message', id: `conversation-${conversationId}` },
        'UnreadCount',
      ],
    }),

    searchConversations: builder.query<Conversation[], string>({
      queryFn: async searchTerm => {
        const service = getMessagingService();
        const conversations = await service.searchConversations(searchTerm);
        return { data: conversations };
      },
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Conversation' as const, id })),
              { type: 'Conversation', id: 'LIST' },
            ]
          : [{ type: 'Conversation', id: 'LIST' }],
    }),

    searchConversationsWithFilters: builder.mutation<Conversation[], ConversationFilterParams>({
      queryFn: async filterParams => {
        const service = getMessagingService();
        const conversations = await service.searchConversationsWithFilters(filterParams);
        return { data: conversations };
      },
      invalidatesTags: [{ type: 'Conversation', id: 'LIST' }],
    }),

    getUnreadCount: builder.query<number, void>({
      queryFn: async () => {
        const service = getMessagingService();
        const count = await service.getUnreadCount();
        return { data: count };
      },
      providesTags: ['UnreadCount'],
    }),

    searchAvailableParticipants: builder.query<
      AvailableParticipant[],
      { searchTerm: string; dealerId?: number }
    >({
      queryFn: async ({ searchTerm, dealerId }) => {
        const service = getMessagingService();
        const participants = await service.searchAvailableParticipants(searchTerm, dealerId);
        return { data: participants };
      },
    }),
  }),
});

export const {
  useGetConversationsQuery,
  useGetConversationByIdQuery,
  useCreateOrGetConversationMutation,
  useSendMessageMutation,
  useMarkConversationAsReadMutation,
  useSearchConversationsQuery,
  useSearchConversationsWithFiltersMutation,
  useSearchAvailableParticipantsQuery,
  useGetUnreadCountQuery,
} = messagingApi;
